import { plural } from '../format';

/**
 * The activity area's words (console redesign, docs/console-redesign-plan.md):
 * the business's Activity tab, the Log page (`log.*`) and the Businesses list
 * (`businesses.*`) — the three screens phase B6 redesigned, which share one
 * thing: they say in sentences what the trail records in codes.
 *
 * One file per area so the redesign's parallel work never edits the same
 * dictionary. `en.ts` and `es.ts` spread these in; the Spanish half is typed
 * against the English one, so a key missing here is a build failure exactly as
 * it is in the main catalog.
 *
 * **The sentences** (`activity.s.*`) are keyed by the audit action they say,
 * one entry per action and a few per outcome (`.off`, `.new`); the subject is
 * not in them — "Jose" or "Their staff" is put in front by
 * `ui/business/activity/sentences.ts`, which decides every word the row
 * supplies. An entry whose fact may be missing takes it as `''` or `-1` and
 * says the shorter sentence, because the activity read drops names and a CLI
 * row may predate a field: the sentence must still be true without it.
 */
export const activityEn = {
  /* ---- who ------------------------------------------------------- */
  'activity.actor.staff': 'Their staff',
  'activity.actor.operatorUnknown': 'An operator',
  'activity.actor.importer': 'The importer',
  'activity.actor.system': 'The system',
  'activity.actor.ci': 'An automated run',
  'activity.someone': 'someone',

  /* ---- small words sentences are built from ----------------------- */
  'activity.anItem': 'an item',
  'activity.named': ({ name }: { name: string }) => `“${name}”`,
  'activity.kind.scheduled': 'a service',
  'activity.kind.stocked': 'a product',
  'activity.kind.prepared': 'a menu item',
  'activity.kind.stay': 'a room',
  'activity.kind.departure': 'a departure',
  'activity.minutes': ({ n }: { n: number }) => `${String(n)} ${plural(n, 'minute', 'minutes')}`,
  'activity.change.price': ({ from, to }: { from: string; to: string }) =>
    from === '' ? `price ${to}` : `price from ${from} to ${to}`,
  'activity.change.length': ({ from, to }: { from: string; to: string }) =>
    from === '' ? `length ${to}` : `length from ${from} to ${to}`,
  'activity.change.renamed': ({ to }: { to: string }) =>
    to === '' ? 'a new name' : `renamed to “${to}”`,
  'activity.change.offSale': 'taken off sale',
  'activity.change.onSale': 'back on sale',
  'activity.change.headline': 'made the one shown first',
  'activity.template': ({ name }: { name: string }) => `${name} template`,
  'activity.env.test': 'test',
  'activity.env.live': 'live',
  'activity.partner.aiconcierge': 'the AI concierge',
  'activity.partner.named': ({ name }: { name: string }) => `the ${name} integration`,
  'activity.domainStatus.pending_dns': 'waiting on DNS',
  'activity.domainStatus.issuing': 'getting its certificate',
  'activity.domainStatus.live': 'live',
  'activity.role.owner': 'owner',
  'activity.role.staff': 'staff',
  'activity.role.readonly': 'read-only staff',
  'activity.job.conformance': 'the partner-contract check',
  'activity.job.import': 'an import',
  'activity.job.importTrial': 'a trial import',
  'activity.job.named': ({ name }: { name: string }) => `the ${name} job`,

  /* ---- the sentences, by action ------------------------------------ */
  'activity.s.tenant.create': ({ name }: { name: string }) =>
    name === '' ? 'created the business' : `created ${name}`,
  'activity.s.tenant.set_status': ({ from, to }: { from: string; to: string }) =>
    from === '' ? `moved the business to ${to}` : `moved the business from ${from} to ${to}`,
  'activity.s.tenant.set_status.some': 'changed the business’s stage',
  'activity.s.tenant.modules.on': ({ list }: { list: string }) => `turned on ${list}`,
  'activity.s.tenant.modules.trial': ({ list }: { list: string }) => `put ${list} on trial`,
  'activity.s.tenant.modules.off': ({ list }: { list: string }) => `turned off ${list}`,
  'activity.s.tenant.modules.none': 'saved the features without changing any',
  'activity.s.tenant.protect.on': 'protected the business, so nothing can wipe it',
  'activity.s.tenant.protect.off': 'took away the business’s protection',
  'activity.s.tenant.delete': ({ name }: { name: string }) =>
    name === '' ? 'deleted the business' : `deleted the business ${name}`,
  'activity.s.tenant.settings': ({ count }: { count: number }) =>
    count <= 0 ? 'saved their settings' : `changed ${String(count)} of their settings`,
  'activity.s.tenant.scheduling_policy':
    'changed their booking rule (gaps, notice, or which times are offered)',
  'activity.s.tenant.locales': ({ list }: { list: string }) =>
    list === '' ? 'changed their languages' : `set their languages to ${list}`,
  'activity.s.tenant.support_session': ({ minutes }: { minutes: number }) =>
    `looked at their staff app, read-only for ${String(minutes)} ${plural(minutes, 'minute', 'minutes')}`,
  'activity.s.tenant.exported': ({ rows }: { rows: number }) =>
    rows < 0
      ? 'exported their data'
      : `exported their data (${String(rows)} ${plural(rows, 'row', 'rows')})`,
  'activity.s.tenant.export_landed': ({ source }: { source: string }) =>
    source === ''
      ? 'loaded exported data into the business'
      : `loaded data exported from ${source}`,
  'activity.s.tenant.view': 'opened the business page',
  'activity.s.tenant.health': 'checked how the business is doing',
  'activity.s.tenant.activity': 'read the activity',
  'activity.s.tenant.readiness': 'checked how close they are to going live',
  'activity.s.fleet.list': 'looked at the businesses list',
  'activity.s.fleet.health': 'checked the businesses list for problems',
  'activity.s.audit.view': 'read the log',

  'activity.s.catalog.view': 'opened what they sell',
  'activity.s.catalog.create': ({ what, details }: { what: string; details: string }) =>
    details === '' ? `added ${what}` : `added ${what} (${details})`,
  'activity.s.catalog.commerce': ({ what, changes }: { what: string; changes: string }) =>
    changes === '' ? `changed ${what}` : `changed ${what}: ${changes}`,
  'activity.s.catalog.presentation': ({ what }: { what: string }) =>
    `changed how ${what} reads on their site`,
  'activity.s.catalog.media': ({ what, count }: { what: string; count: number }) =>
    count === 0
      ? `removed the photos of ${what}`
      : count < 0
        ? `changed the photos of ${what}`
        : `set ${String(count)} ${plural(count, 'photo', 'photos')} for ${what}`,
  'activity.s.catalog.category': ({
    from,
    to,
    count,
  }: {
    from: string;
    to: string;
    count: number;
  }) =>
    (from === '' ? 'renamed a heading' : `renamed the heading “${from}” to “${to}”`) +
    (count > 0 ? ` (${String(count)} ${plural(count, 'item', 'items')})` : ''),
  'activity.s.catalog.option.add': ({ details }: { details: string }) =>
    details === '' ? 'added a length to a service' : `added a length to a service (${details})`,
  'activity.s.catalog.option.change': ({ changes }: { changes: string }) =>
    changes === '' ? 'changed a length of a service' : `changed a length of a service: ${changes}`,
  'activity.s.catalog.add_on.link': 'linked an add-on to a service',
  'activity.s.catalog.add_on.change': 'changed what an add-on costs with a service',
  'activity.s.catalog.add_on.unlink': 'unlinked an add-on from a service',
  'activity.s.catalog.merge_options': ({ rows, lengths }: { rows: number; lengths: number }) =>
    rows <= 1
      ? 'merged rows of a service into one with several lengths'
      : lengths <= 0
        ? `merged ${String(rows)} rows into one service with several lengths`
        : `merged ${String(rows)} rows into one service with ${String(lengths)} ${plural(lengths, 'length', 'lengths')}`,
  'activity.s.catalog.roles': 'changed who can do a service',
  'activity.s.catalog.seed': ({ count }: { count: number }) =>
    count < 0
      ? 'loaded what they sell from a file'
      : `loaded ${String(count)} ${plural(count, 'item', 'items')} from a catalog file`,
  'activity.s.catalog.seed.dry': 'tried a catalog file without saving it',

  'activity.s.intake.mint': ({ details }: { details: string }) =>
    details === '' ? 'made a questionnaire link' : `made a questionnaire link (${details})`,
  'activity.s.intake.mint.rotated': ({ details }: { details: string }) =>
    `made a new questionnaire link${details === '' ? '' : ` (${details})`}; the old one stopped working`,
  'activity.s.intake.extend': ({ days }: { days: number }) =>
    days <= 0
      ? 'gave the owner more time to answer the questionnaire'
      : `gave the owner ${String(days)} more ${plural(days, 'day', 'days')} to answer the questionnaire`,
  'activity.s.intake.reopen': 'reopened the questionnaire so the owner can change their answers',
  'activity.s.intake.close': 'closed the questionnaire link',
  'activity.s.intake.apply_hours': 'set their opening hours from the questionnaire',
  'activity.s.intake.apply_closures': ({ count }: { count: number }) =>
    count < 0
      ? 'added closed days from the questionnaire'
      : `added ${String(count)} closed ${plural(count, 'day', 'days')} from the questionnaire`,
  'activity.s.intake.remove_closure': 'removed a closed day added from the questionnaire',
  'activity.s.intake.promote': 'put one of the owner’s photos on their site',
  'activity.s.intake.reject': 'turned down one of the owner’s photos',
  'activity.s.intake.view': 'read the questionnaire answers',
  'activity.s.intake.list': 'looked at the questionnaires out with owners',
  'activity.s.intake.uploads': 'looked at the owner’s photos',
  'activity.s.intake.draft': 'previewed the site built from the questionnaire',
  'activity.s.intake.catalog_draft': 'previewed the services from the questionnaire',

  'activity.s.key.issue': ({ env, partner }: { env: string; partner: string }) =>
    env === '' ? `made an access key for ${partner}` : `made a ${env} access key for ${partner}`,
  'activity.s.key.revoke': ({ partner }: { partner: string }) =>
    `revoked an access key for ${partner}`,
  'activity.s.key.list': 'looked at the partner access keys',
  'activity.s.webhook.set.new': ({ partner }: { partner: string }) =>
    `started sending booking updates to ${partner}`,
  'activity.s.webhook.set.change': ({ partner }: { partner: string }) =>
    `changed where booking updates go for ${partner}`,
  'activity.s.webhook.set.off': ({ partner }: { partner: string }) =>
    `stopped booking updates to ${partner}`,
  'activity.s.webhook.list': 'looked at booking updates to partners',
  'activity.s.delivery.list': 'looked at the booking updates sent',
  'activity.s.delivery.requeue': ({ since, count }: { since: string; count: number }) =>
    (since === '' ? 'resent booking updates' : `resent booking updates since ${since}`) +
    (count < 0 ? '' : ` (${String(count)})`),

  'activity.s.domain.add': ({ host }: { host: string }) => `added the website address ${host}`,
  'activity.s.domain.add.moved': ({ host }: { host: string }) =>
    `moved the website address ${host} here from another business`,
  'activity.s.domain.status': ({ host, status }: { host: string; status: string }) =>
    `marked ${host} as ${status}`,
  'activity.s.domain.remove': ({ host }: { host: string }) => `removed the website address ${host}`,
  'activity.s.domain.list': 'looked at the website addresses',
  'activity.s.branding.publish': ({ version }: { version: number }) =>
    version < 0
      ? 'published the website design'
      : `published the website design (version ${String(version)})`,
  'activity.s.branding.rollback': ({ version }: { version: number }) =>
    version < 0
      ? 'brought back an earlier website design'
      : `brought back website design version ${String(version)}`,
  'activity.s.branding.view': 'opened the website design',
  'activity.s.branding.app_look': 'changed how their staff app looks',
  'activity.s.branding.app_look.cleared': 'put their staff app back to its standard look',
  'activity.s.tenant.asset_upload.start': ({ what }: { what: string }) =>
    `started uploading ${what}`,
  'activity.s.tenant.asset_upload': ({ what }: { what: string }) => `uploaded ${what}`,
  'activity.asset.staffBackground': 'a background photo for their staff app',
  'activity.asset.other': 'a picture for the business',
  'activity.s.catalog.media_upload.start': ({ what }: { what: string }) =>
    `started uploading a photo of ${what}`,
  'activity.s.catalog.media_upload': ({ what }: { what: string }) => `uploaded a photo of ${what}`,
  'activity.s.item.photo.add': ({ what }: { what: string }) => `added a photo to ${what}`,
  'activity.s.item.photo.arrange': ({ what }: { what: string }) =>
    `rearranged the photos of ${what}`,
  'activity.s.item.photo.arrange.removed': ({ what, count }: { what: string; count: number }) =>
    `removed ${String(count)} ${plural(count, 'photo', 'photos')} of ${what}`,
  'activity.s.item.photo.arrange.website': ({ what }: { what: string }) =>
    `changed which photos of ${what} show on the website`,
  'activity.s.tenant.catalog_editors.owner': 'let only the owner change what they sell',
  'activity.s.tenant.catalog_editors.staff': 'let their staff change what they sell, too',
  'activity.s.tenant.catalog_editors': 'changed who may change what they sell',

  'activity.s.concierge.enable': 'turned on the AI concierge',
  'activity.s.concierge.disable': 'turned off the AI concierge',
  'activity.s.concierge.settings': 'changed the AI concierge’s settings',
  'activity.s.concierge.status.paused': 'paused the AI concierge',
  'activity.s.concierge.status.enabled': 'resumed the AI concierge',
  'activity.s.concierge.sync': 'had the AI concierge re-read their services and profile',
  'activity.s.concierge.view': 'checked on the AI concierge',
  'activity.s.staff.list': 'looked at who can sign in',

  'activity.s.job.run': ({ job }: { job: string }) => `started ${job}`,
  'activity.s.job.executions': ({ job }: { job: string }) => `looked at recent runs of ${job}`,
  'activity.s.job.execution': ({ job }: { job: string }) => `looked at one run of ${job}`,

  'activity.s.staff.create': ({ who, role }: { who: string; role: string }) =>
    `gave ${who} a staff login as ${role}`,
  'activity.s.staff.invite': ({ who, role }: { who: string; role: string }) =>
    `invited ${who} to the staff app as ${role}`,
  'activity.s.staff.resend': ({ who }: { who: string }) =>
    `sent the staff app invitation to ${who} again`,
  'activity.s.staff.revoke': ({ who }: { who: string }) => `took away ${who}’s staff login`,
  'activity.s.staff.remove': ({ who }: { who: string }) => `removed ${who} from their staff`,
  'activity.s.staff.set_role': ({ who, role }: { who: string; role: string }) =>
    `made ${who} ${role}`,
  'activity.s.staff.disable': ({ who }: { who: string }) => `paused ${who}’s staff login`,
  'activity.s.staff.enable': ({ who }: { who: string }) => `turned ${who}’s staff login back on`,
  'activity.s.staff.record': ({ who, role }: { who: string; role: string }) =>
    `recorded ${who}’s staff login as ${role}`,
  'activity.s.operator.grant': ({ who }: { who: string }) => `gave ${who} access to this console`,
  'activity.s.operator.revoke': ({ who }: { who: string }) =>
    `took away ${who}’s access to this console`,

  'activity.s.album.create': 'made a photo album for a tour',
  'activity.s.album.delete': ({ count }: { count: number }) =>
    count < 0
      ? 'deleted a photo album'
      : `deleted a photo album (${String(count)} ${plural(count, 'photo', 'photos')})`,
  'activity.s.album.notify': 'sent a guest the link to their photo album',
  'activity.s.album.regenerate': 'made a new link for a photo album',
  'activity.s.album.revoke': 'turned off a photo album’s link',
  'activity.s.client.anonymize': 'anonymized a client’s record',
  'activity.s.data_request.file': 'filed a client’s request about their data',
  'activity.s.data_request.file.export': 'filed a client’s request for a copy of their data',
  'activity.s.data_request.file.delete': 'filed a client’s request to delete their data',
  'activity.s.data_request.run': 'carried out a client’s data request',
  'activity.s.data_request.cancel': 'cancelled a client’s data request',
  'activity.s.departures.waiver_published': ({ version }: { version: number }) =>
    version < 0 ? 'published the waiver' : `published version ${String(version)} of the waiver`,
  'activity.s.departures.waiver_withdrawn': 'withdrew the waiver',
  'activity.s.lodging.ical_feed_mint': 'made a calendar feed for a room',
  'activity.s.lodging.ical_feed_revoke': 'turned off a room’s calendar feed',

  'activity.s.messaging.newsletter': 'changed the newsletter settings',
  'activity.s.messaging.newsletter.off': 'turned off the newsletter',
  'activity.s.messaging.owner': 'changed who texts their clients',
  'activity.s.messaging.owner_alerts': 'changed the alerts the owner gets',
  'activity.s.messaging.reminders': 'changed appointment reminders',
  'activity.s.ordering.policy': 'changed the ordering rules',
  'activity.s.ordering.timing': 'changed when orders can be placed',
  'activity.s.payment.config': 'set up PayPal payments',
  'activity.s.payment.config.test': 'set up PayPal payments in test mode',
  'activity.s.fulfillment.credentials': 'connected the print shop',
  'activity.s.shop.fulfilment': 'changed how shop orders are fulfilled',
  'activity.s.shop.payment': 'changed how shop orders are paid for',
  'activity.s.shop.pickup_expiry': 'changed how long orders wait to be picked up',
  'activity.s.till.tax': 'changed the sales tax',
  'activity.s.till.tax.off': 'turned off sales tax',
  'activity.s.till.tenders': 'changed the payment buttons at the counter',
  'activity.s.till.tips': 'changed tipping at the counter',
  'activity.s.booking.mode': 'changed how guests book online',
  'activity.s.scheduling.grid': 'changed the times bookings can start',

  'activity.s.import.run': 'imported their old system',
  'activity.s.import.run.ok': 'imported their old system, and the totals matched',
  'activity.s.import.run.off': 'imported their old system, and the totals did not match',
  'activity.s.import.appointments': ({ booked, refused }: { booked: number; refused: number }) =>
    booked < 0
      ? 'imported appointments'
      : `imported ${String(booked)} ${plural(booked, 'appointment', 'appointments')}` +
        (refused > 0 ? ` (${String(refused)} could not be booked)` : ''),
  'activity.s.import.contacts': ({ created, matched }: { created: number; matched: number }) =>
    created < 0
      ? 'imported contacts'
      : `imported contacts: ${String(created)} new, ${String(matched)} already there`,
  'activity.s.import.clover_customers': ({
    created,
    matched,
  }: {
    created: number;
    matched: number;
  }) =>
    created < 0
      ? 'imported clients from Clover'
      : `imported clients from Clover: ${String(created)} new, ${String(matched)} already there`,
  'activity.s.roster.seed': 'reset a test business',
  'activity.s.roster.reset': 'reset the contract-check business for today',
  'activity.s.unknown': ({ verb, noun }: { verb: string; noun: string }) =>
    `did “${verb}” (${noun})`,

  /* ---- the feed ---------------------------------------------------- */
  'activity.title': 'Activity',
  'activity.who': 'Who',
  'activity.who.everyone': 'Everyone',
  'activity.who.business': 'The business',
  'activity.who.operators': 'Operators',
  'activity.looks': 'Include when someone only looked',
  'activity.newer': 'Newer',
  'activity.older': 'Older',
  'activity.empty': 'Nothing yet.',
  'activity.emptyFiltered': 'Nothing matches.',
  'activity.raw': 'Raw record',
  'activity.raw.action': 'Action',
  'activity.raw.actor': 'By',
  'activity.raw.target': 'About',
  'activity.raw.at': 'When',
  'activity.raw.detail': 'Detail',
  'activity.raw.none': '(nothing)',
  'activity.loading': 'the activity',

  /* ---- the log ----------------------------------------------------- */
  'log.title': 'Log',
  'log.lede': 'Who did what, across every business.',
  'log.filter.business': 'Business',
  'log.filter.businessHint': 'Any business (its id)',
  'log.filter.person': 'Person',
  'log.filter.personHint': 'Anyone',
  'log.filter.kind': 'Kind of change',
  'log.kind.all': 'Everything',
  'log.kind.stage': 'Businesses and stages',
  'log.kind.features': 'Features',
  'log.kind.settings': 'Settings',
  'log.kind.catalog': 'What they sell',
  'log.kind.website': 'Website and questionnaire',
  'log.kind.connections': 'Keys and partners',
  'log.kind.people': 'Logins and support looks',
  'log.kind.data': 'Imports, exports and checks',
  'log.filter.since': 'Since',
  'log.since.any': 'Any time',
  'log.since.day': 'The last 24 hours',
  'log.since.week': 'The last 7 days',
  'log.since.month': 'The last 30 days',
  'log.clear': 'Clear filters',
  'log.selfNote': 'Reading the log is itself recorded, so your own reads show up here too.',
  'log.allBusinesses': 'All businesses',
  'log.empty': 'Nothing matches.',
  'log.loading': 'the log',

  /* ---- the businesses list ----------------------------------------- */
  'businesses.title': 'Businesses',
  'businesses.count': ({ total }: { total: number }) =>
    `${String(total)} ${plural(total, 'business', 'businesses')}`,
  'businesses.needAttention': ({ count }: { count: number }) =>
    `${String(count)} ${plural(count, 'needs', 'need')} your attention`,
  'businesses.search': 'Find a business',
  'businesses.stage': 'Stage',
  'businesses.stage.all': 'All',
  'businesses.stage.setup': 'Setting up',
  'businesses.stage.live': 'Live',
  'businesses.stage.attention': 'Needs attention',
  'businesses.showTest': 'Show test businesses',
  'businesses.add': 'Add a business',
  'businesses.list': 'Businesses',
  'businesses.col.stage': 'Stage',
  'businesses.col.next': 'Next',
  'businesses.col.health': 'Health',
  'businesses.demo': 'demo',
  'businesses.test': 'test',
  'businesses.protected': 'Protected',
  'businesses.health.ok': 'Nothing wrong',
  'businesses.health.attention': ({ count }: { count: number }) =>
    count <= 0 ? 'Worth a look' : `${String(count)} to look at`,
  'businesses.health.failing': ({ count }: { count: number }) =>
    count <= 0 ? 'Something is wrong' : `${String(count)} ${plural(count, 'problem', 'problems')}`,
  'businesses.health.unknown': 'Not checked',
  'businesses.health.why': 'Why',
  'businesses.healthUnavailable':
    'Health could not be read, so no business is shown as fine until it can be.',
  'businesses.sortWorst': 'Worst first',
  'businesses.attentionPageOnly': 'Only the businesses on this page are checked for problems.',
  'businesses.empty': 'No businesses yet. Add one to begin.',
  'businesses.emptyFiltered': 'No business matches.',
  'businesses.emptyAttention': 'Nothing on this page needs your attention.',
  'businesses.next.fix': 'Fix:',
  'businesses.next.more': ({ count }: { count: number }) => ` and ${String(count)} more`,
  'businesses.next.waiting': 'Waiting on the owner',
  'businesses.next.questionnaire': ({ answered, total }: { answered: number; total: number }) =>
    total <= 0
      ? 'to open their questionnaire'
      : `to finish their questionnaire (${String(answered)} of ${String(total)} answered)`,
  'businesses.next.review': 'Review',
  'businesses.next.reviewWhat': 'the questionnaire answers they sent back',
  'businesses.next.setup': 'Keep setting up:',
  'businesses.next.setupWhat': 'what they sell, their hours and their logins',
  'businesses.next.importing': 'Moving their book in:',
  'businesses.next.importingWhat': 'go live once it checks out',
  'businesses.next.paused': 'Paused:',
  'businesses.next.pausedWhat': 'resume when they are ready',
  'businesses.next.offboarded': 'Nothing to do — they have left',
  'businesses.next.nothing': 'Nothing to do',
  'businesses.next.checking': 'Checking…',
  'businesses.reason.webhook_exhausted': 'booking updates to a partner stopped arriving',
  'businesses.reason.endpoint_failing': 'a partner’s address for booking updates keeps failing',
  'businesses.reason.webhook_failed': 'a booking update to a partner failed and is being retried',
  'businesses.reason.webhook_stuck':
    'booking updates to a partner have waited more than half an hour to send',
  'businesses.reason.text_exhausted':
    'a text to a client could not be sent, even after every retry',
  'businesses.reason.text_failed': 'a text to a client failed and is being retried',
  'businesses.reason.text_stuck': 'texts to clients have waited more than half an hour to send',
  'businesses.reason.request_overdue': 'a booking request ran out of time with no answer',
  'businesses.reason.site_not_served': 'their website address is not working',
};

export const activityEs: typeof activityEn = {
  'activity.actor.staff': 'Su personal',
  'activity.actor.operatorUnknown': 'Un operador',
  'activity.actor.importer': 'El importador',
  'activity.actor.system': 'El sistema',
  'activity.actor.ci': 'Una ejecución automática',
  'activity.someone': 'alguien',

  'activity.anItem': 'un artículo',
  'activity.named': ({ name }) => `«${name}»`,
  'activity.kind.scheduled': 'un servicio',
  'activity.kind.stocked': 'un producto',
  'activity.kind.prepared': 'un plato del menú',
  'activity.kind.stay': 'una habitación',
  'activity.kind.departure': 'una salida',
  'activity.minutes': ({ n }) => `${String(n)} ${plural(n, 'minuto', 'minutos')}`,
  'activity.change.price': ({ from, to }) =>
    from === '' ? `precio ${to}` : `precio de ${from} a ${to}`,
  'activity.change.length': ({ from, to }) =>
    from === '' ? `duración ${to}` : `duración de ${from} a ${to}`,
  'activity.change.renamed': ({ to }) => (to === '' ? 'otro nombre' : `ahora se llama «${to}»`),
  'activity.change.offSale': 'retirado de la venta',
  'activity.change.onSale': 'de nuevo a la venta',
  'activity.change.headline': 'ahora es la que se muestra primero',
  'activity.template': ({ name }) => `plantilla ${name}`,
  'activity.env.test': 'de prueba',
  'activity.env.live': 'real',
  'activity.partner.aiconcierge': 'el conserje de IA',
  'activity.partner.named': ({ name }) => `la integración ${name}`,
  'activity.domainStatus.pending_dns': 'esperando el DNS',
  'activity.domainStatus.issuing': 'obteniendo su certificado',
  'activity.domainStatus.live': 'en marcha',
  'activity.role.owner': 'dueño o dueña',
  'activity.role.staff': 'personal',
  'activity.role.readonly': 'personal de solo lectura',
  'activity.job.conformance': 'la comprobación del contrato con socios',
  'activity.job.import': 'una importación',
  'activity.job.importTrial': 'una importación de prueba',
  'activity.job.named': ({ name }) => `el trabajo ${name}`,

  'activity.s.tenant.create': ({ name }) => (name === '' ? 'creó el negocio' : `creó ${name}`),
  'activity.s.tenant.set_status': ({ from, to }) =>
    from === '' ? `pasó el negocio a ${to}` : `pasó el negocio de ${from} a ${to}`,
  'activity.s.tenant.set_status.some': 'cambió la etapa del negocio',
  'activity.s.tenant.modules.on': ({ list }) => `activó ${list}`,
  'activity.s.tenant.modules.trial': ({ list }) => `puso ${list} a prueba`,
  'activity.s.tenant.modules.off': ({ list }) => `desactivó ${list}`,
  'activity.s.tenant.modules.none': 'guardó las funciones sin cambiar ninguna',
  'activity.s.tenant.protect.on': 'protegió el negocio para que nada pueda borrarlo',
  'activity.s.tenant.protect.off': 'le quitó la protección al negocio',
  'activity.s.tenant.delete': ({ name }) =>
    name === '' ? 'eliminó el negocio' : `eliminó el negocio ${name}`,
  'activity.s.tenant.settings': ({ count }) =>
    count <= 0 ? 'guardó sus ajustes' : `cambió ${String(count)} de sus ajustes`,
  'activity.s.tenant.scheduling_policy':
    'cambió su regla de reservas (márgenes, antelación u horarios que se ofrecen)',
  'activity.s.tenant.locales': ({ list }) =>
    list === '' ? 'cambió sus idiomas' : `definió sus idiomas: ${list}`,
  'activity.s.tenant.support_session': ({ minutes }) =>
    `miró su app del personal, en solo lectura durante ${String(minutes)} ${plural(minutes, 'minuto', 'minutos')}`,
  'activity.s.tenant.exported': ({ rows }) =>
    rows < 0
      ? 'exportó sus datos'
      : `exportó sus datos (${String(rows)} ${plural(rows, 'fila', 'filas')})`,
  'activity.s.tenant.export_landed': ({ source }) =>
    source === '' ? 'cargó datos exportados en el negocio' : `cargó datos exportados de ${source}`,
  'activity.s.tenant.view': 'abrió la página del negocio',
  'activity.s.tenant.health': 'revisó cómo va el negocio',
  'activity.s.tenant.activity': 'leyó la actividad',
  'activity.s.tenant.readiness': 'revisó cuánto les falta para ponerse en marcha',
  'activity.s.fleet.list': 'miró la lista de negocios',
  'activity.s.fleet.health': 'buscó problemas en la lista de negocios',
  'activity.s.audit.view': 'leyó el registro',

  'activity.s.catalog.view': 'abrió lo que venden',
  'activity.s.catalog.create': ({ what, details }) =>
    details === '' ? `añadió ${what}` : `añadió ${what} (${details})`,
  'activity.s.catalog.commerce': ({ what, changes }) =>
    changes === '' ? `cambió ${what}` : `cambió ${what}: ${changes}`,
  'activity.s.catalog.presentation': ({ what }) => `cambió cómo se presenta ${what} en su sitio`,
  'activity.s.catalog.media': ({ what, count }) =>
    count === 0
      ? `quitó las fotos de ${what}`
      : count < 0
        ? `cambió las fotos de ${what}`
        : `puso ${String(count)} ${plural(count, 'foto', 'fotos')} en ${what}`,
  'activity.s.catalog.category': ({ from, to, count }) =>
    (from === ''
      ? 'cambió el nombre de un encabezado'
      : `cambió el encabezado «${from}» por «${to}»`) +
    (count > 0 ? ` (${String(count)} ${plural(count, 'artículo', 'artículos')})` : ''),
  'activity.s.catalog.option.add': ({ details }) =>
    details === ''
      ? 'añadió una duración a un servicio'
      : `añadió una duración a un servicio (${details})`,
  'activity.s.catalog.option.change': ({ changes }) =>
    changes === ''
      ? 'cambió una duración de un servicio'
      : `cambió una duración de un servicio: ${changes}`,
  'activity.s.catalog.add_on.link': 'vinculó un complemento a un servicio',
  'activity.s.catalog.add_on.change': 'cambió lo que cuesta un complemento con un servicio',
  'activity.s.catalog.add_on.unlink': 'desvinculó un complemento de un servicio',
  'activity.s.catalog.merge_options': ({ rows, lengths }) =>
    rows <= 1
      ? 'unió filas de un servicio en uno con varias duraciones'
      : lengths <= 0
        ? `unió ${String(rows)} filas en un servicio con varias duraciones`
        : `unió ${String(rows)} filas en un servicio con ${String(lengths)} ${plural(lengths, 'duración', 'duraciones')}`,
  'activity.s.catalog.roles': 'cambió quién puede hacer un servicio',
  'activity.s.catalog.seed': ({ count }) =>
    count < 0
      ? 'cargó lo que venden desde un archivo'
      : `cargó ${String(count)} ${plural(count, 'artículo', 'artículos')} desde un archivo de catálogo`,
  'activity.s.catalog.seed.dry': 'probó un archivo de catálogo sin guardarlo',

  'activity.s.intake.mint': ({ details }) =>
    details === ''
      ? 'creó un enlace al cuestionario'
      : `creó un enlace al cuestionario (${details})`,
  'activity.s.intake.mint.rotated': ({ details }) =>
    `creó un nuevo enlace al cuestionario${details === '' ? '' : ` (${details})`}; el anterior dejó de funcionar`,
  'activity.s.intake.extend': ({ days }) =>
    days <= 0
      ? 'le dio más tiempo al dueño o la dueña para responder el cuestionario'
      : `le dio ${String(days)} ${plural(days, 'día', 'días')} más al dueño o la dueña para responder el cuestionario`,
  'activity.s.intake.reopen':
    'reabrió el cuestionario para que el dueño o la dueña pueda cambiar sus respuestas',
  'activity.s.intake.close': 'cerró el enlace al cuestionario',
  'activity.s.intake.apply_hours': 'fijó su horario a partir del cuestionario',
  'activity.s.intake.apply_closures': ({ count }) =>
    count < 0
      ? 'añadió días cerrados a partir del cuestionario'
      : `añadió ${String(count)} ${plural(count, 'día cerrado', 'días cerrados')} a partir del cuestionario`,
  'activity.s.intake.remove_closure': 'quitó un día cerrado añadido desde el cuestionario',
  'activity.s.intake.promote': 'puso una de sus fotos en su sitio',
  'activity.s.intake.reject': 'rechazó una de sus fotos',
  'activity.s.intake.view': 'leyó las respuestas del cuestionario',
  'activity.s.intake.list': 'miró los cuestionarios pendientes',
  'activity.s.intake.uploads': 'miró sus fotos',
  'activity.s.intake.draft': 'previsualizó el sitio hecho con el cuestionario',
  'activity.s.intake.catalog_draft': 'previsualizó los servicios del cuestionario',

  'activity.s.key.issue': ({ env, partner }) =>
    env === ''
      ? `creó una clave de acceso para ${partner}`
      : `creó una clave de acceso ${env} para ${partner}`,
  'activity.s.key.revoke': ({ partner }) => `revocó una clave de acceso de ${partner}`,
  'activity.s.key.list': 'miró las claves de acceso de socios',
  'activity.s.webhook.set.new': ({ partner }) => `empezó a enviar avisos de reservas a ${partner}`,
  'activity.s.webhook.set.change': ({ partner }) =>
    `cambió adónde van los avisos de reservas de ${partner}`,
  'activity.s.webhook.set.off': ({ partner }) => `dejó de enviar avisos de reservas a ${partner}`,
  'activity.s.webhook.list': 'miró los avisos de reservas a socios',
  'activity.s.delivery.list': 'miró los avisos de reservas enviados',
  'activity.s.delivery.requeue': ({ since, count }) =>
    (since === ''
      ? 'reenvió avisos de reservas'
      : `reenvió los avisos de reservas desde ${since}`) + (count < 0 ? '' : ` (${String(count)})`),

  'activity.s.domain.add': ({ host }) => `añadió la dirección web ${host}`,
  'activity.s.domain.add.moved': ({ host }) => `trajo la dirección web ${host} desde otro negocio`,
  'activity.s.domain.status': ({ host, status }) => `marcó ${host} como ${status}`,
  'activity.s.domain.remove': ({ host }) => `quitó la dirección web ${host}`,
  'activity.s.domain.list': 'miró las direcciones web',
  'activity.s.branding.publish': ({ version }) =>
    version < 0
      ? 'publicó el diseño del sitio'
      : `publicó el diseño del sitio (versión ${String(version)})`,
  'activity.s.branding.rollback': ({ version }) =>
    version < 0
      ? 'recuperó un diseño anterior del sitio'
      : `recuperó la versión ${String(version)} del diseño del sitio`,
  'activity.s.branding.view': 'abrió el diseño del sitio',
  'activity.s.branding.app_look': 'cambió el aspecto de su app del personal',
  'activity.s.branding.app_look.cleared': 'devolvió su app del personal al aspecto de siempre',
  'activity.s.tenant.asset_upload.start': ({ what }) => `empezó a subir ${what}`,
  'activity.s.tenant.asset_upload': ({ what }) => `subió ${what}`,
  'activity.asset.staffBackground': 'una foto de fondo para su app del personal',
  'activity.asset.other': 'una imagen del negocio',
  'activity.s.catalog.media_upload.start': ({ what }) => `empezó a subir una foto de ${what}`,
  'activity.s.catalog.media_upload': ({ what }) => `subió una foto de ${what}`,
  'activity.s.item.photo.add': ({ what }) => `añadió una foto a ${what}`,
  'activity.s.item.photo.arrange': ({ what }) => `reordenó las fotos de ${what}`,
  'activity.s.item.photo.arrange.removed': ({ what, count }) =>
    `quitó ${String(count)} ${plural(count, 'foto', 'fotos')} de ${what}`,
  'activity.s.item.photo.arrange.website': ({ what }) =>
    `cambió qué fotos de ${what} se ven en el sitio web`,
  'activity.s.tenant.catalog_editors.owner':
    'dejó que solo el dueño o la dueña cambie lo que venden',
  'activity.s.tenant.catalog_editors.staff': 'dejó que su personal también cambie lo que venden',
  'activity.s.tenant.catalog_editors': 'cambió quién puede cambiar lo que venden',

  'activity.s.concierge.enable': 'activó el conserje de IA',
  'activity.s.concierge.disable': 'desactivó el conserje de IA',
  'activity.s.concierge.settings': 'cambió los ajustes del conserje de IA',
  'activity.s.concierge.status.paused': 'pausó el conserje de IA',
  'activity.s.concierge.status.enabled': 'reanudó el conserje de IA',
  'activity.s.concierge.sync':
    'hizo que el conserje de IA volviera a leer sus servicios y su perfil',
  'activity.s.concierge.view': 'revisó el conserje de IA',
  'activity.s.staff.list': 'revisó quién puede entrar',

  'activity.s.job.run': ({ job }) => `inició ${job}`,
  'activity.s.job.executions': ({ job }) => `miró las últimas ejecuciones de ${job}`,
  'activity.s.job.execution': ({ job }) => `miró una ejecución de ${job}`,

  'activity.s.staff.create': ({ who, role }) =>
    `le dio a ${who} un acceso del personal como ${role}`,
  'activity.s.staff.invite': ({ who, role }) =>
    `invitó a ${who} a la app del personal como ${role}`,
  'activity.s.staff.resend': ({ who }) =>
    `volvió a enviar la invitación a la app del personal a ${who}`,
  'activity.s.staff.revoke': ({ who }) => `le quitó el acceso del personal a ${who}`,
  'activity.s.staff.remove': ({ who }) => `quitó a ${who} de su personal`,
  'activity.s.staff.set_role': ({ who, role }) => `cambió el rol de ${who} a ${role}`,
  'activity.s.staff.disable': ({ who }) => `pausó el acceso del personal de ${who}`,
  'activity.s.staff.enable': ({ who }) => `reactivó el acceso del personal de ${who}`,
  'activity.s.staff.record': ({ who, role }) =>
    `registró el acceso del personal de ${who} como ${role}`,
  'activity.s.operator.grant': ({ who }) => `le dio a ${who} acceso a esta consola`,
  'activity.s.operator.revoke': ({ who }) => `le quitó a ${who} el acceso a esta consola`,

  'activity.s.album.create': 'creó un álbum de fotos de una excursión',
  'activity.s.album.delete': ({ count }) =>
    count < 0
      ? 'eliminó un álbum de fotos'
      : `eliminó un álbum de fotos (${String(count)} ${plural(count, 'foto', 'fotos')})`,
  'activity.s.album.notify': 'le envió a un cliente el enlace a su álbum de fotos',
  'activity.s.album.regenerate': 'creó un nuevo enlace para un álbum de fotos',
  'activity.s.album.revoke': 'desactivó el enlace de un álbum de fotos',
  'activity.s.client.anonymize': 'anonimizó la ficha de un cliente',
  'activity.s.data_request.file': 'registró una solicitud de un cliente sobre sus datos',
  'activity.s.data_request.file.export':
    'registró la solicitud de un cliente de una copia de sus datos',
  'activity.s.data_request.file.delete': 'registró la solicitud de un cliente de borrar sus datos',
  'activity.s.data_request.run': 'atendió la solicitud de datos de un cliente',
  'activity.s.data_request.cancel': 'canceló la solicitud de datos de un cliente',
  'activity.s.departures.waiver_published': ({ version }) =>
    version < 0 ? 'publicó la exención' : `publicó la versión ${String(version)} de la exención`,
  'activity.s.departures.waiver_withdrawn': 'retiró la exención',
  'activity.s.lodging.ical_feed_mint': 'creó un calendario compartido para una habitación',
  'activity.s.lodging.ical_feed_revoke': 'desactivó el calendario compartido de una habitación',

  'activity.s.messaging.newsletter': 'cambió los ajustes del boletín',
  'activity.s.messaging.newsletter.off': 'desactivó el boletín',
  'activity.s.messaging.owner': 'cambió quién envía mensajes a sus clientes',
  'activity.s.messaging.owner_alerts': 'cambió los avisos que recibe el dueño o la dueña',
  'activity.s.messaging.reminders': 'cambió los recordatorios de citas',
  'activity.s.ordering.policy': 'cambió las reglas de los pedidos',
  'activity.s.ordering.timing': 'cambió cuándo se pueden hacer pedidos',
  'activity.s.payment.config': 'configuró los pagos con PayPal',
  'activity.s.payment.config.test': 'configuró los pagos con PayPal en modo de prueba',
  'activity.s.fulfillment.credentials': 'conectó la imprenta',
  'activity.s.shop.fulfilment': 'cambió cómo se preparan los pedidos de la tienda',
  'activity.s.shop.payment': 'cambió cómo se pagan los pedidos de la tienda',
  'activity.s.shop.pickup_expiry': 'cambió cuánto esperan los pedidos a ser recogidos',
  'activity.s.till.tax': 'cambió el impuesto sobre las ventas',
  'activity.s.till.tax.off': 'desactivó el impuesto sobre las ventas',
  'activity.s.till.tenders': 'cambió los botones de pago del mostrador',
  'activity.s.till.tips': 'cambió las propinas en el mostrador',
  'activity.s.booking.mode': 'cambió cómo reservan los clientes en línea',
  'activity.s.scheduling.grid': 'cambió las horas a las que pueden empezar las reservas',

  'activity.s.import.run': 'importó su sistema anterior',
  'activity.s.import.run.ok': 'importó su sistema anterior, y los totales cuadraron',
  'activity.s.import.run.off': 'importó su sistema anterior, y los totales no cuadraron',
  'activity.s.import.appointments': ({ booked, refused }) =>
    booked < 0
      ? 'importó citas'
      : `importó ${String(booked)} ${plural(booked, 'cita', 'citas')}` +
        (refused > 0 ? ` (${String(refused)} no se pudieron reservar)` : ''),
  'activity.s.import.contacts': ({ created, matched }) =>
    created < 0
      ? 'importó contactos'
      : `importó contactos: ${String(created)} nuevos, ${String(matched)} ya estaban`,
  'activity.s.import.clover_customers': ({ created, matched }) =>
    created < 0
      ? 'importó clientes de Clover'
      : `importó clientes de Clover: ${String(created)} nuevos, ${String(matched)} ya estaban`,
  'activity.s.roster.seed': 'restableció un negocio de prueba',
  'activity.s.roster.reset': 'restableció para hoy el negocio de la comprobación del contrato',
  'activity.s.unknown': ({ verb, noun }) => `hizo «${verb}» (${noun})`,

  'activity.title': 'Actividad',
  'activity.who': 'Quién',
  'activity.who.everyone': 'Todos',
  'activity.who.business': 'El negocio',
  'activity.who.operators': 'Operadores',
  'activity.looks': 'Incluir cuando alguien solo miró',
  'activity.newer': 'Más recientes',
  'activity.older': 'Más antiguas',
  'activity.empty': 'Nada todavía.',
  'activity.emptyFiltered': 'Nada coincide.',
  'activity.raw': 'Registro original',
  'activity.raw.action': 'Acción',
  'activity.raw.actor': 'Por',
  'activity.raw.target': 'Sobre',
  'activity.raw.at': 'Cuándo',
  'activity.raw.detail': 'Detalle',
  'activity.raw.none': '(nada)',
  'activity.loading': 'la actividad',

  'log.title': 'Registro',
  'log.lede': 'Quién hizo qué, en todos los negocios.',
  'log.filter.business': 'Negocio',
  'log.filter.businessHint': 'Cualquier negocio (su id)',
  'log.filter.person': 'Persona',
  'log.filter.personHint': 'Cualquiera',
  'log.filter.kind': 'Tipo de cambio',
  'log.kind.all': 'Todo',
  'log.kind.stage': 'Negocios y etapas',
  'log.kind.features': 'Funciones',
  'log.kind.settings': 'Ajustes',
  'log.kind.catalog': 'Lo que venden',
  'log.kind.website': 'Sitio web y cuestionario',
  'log.kind.connections': 'Claves y socios',
  'log.kind.people': 'Accesos y miradas de soporte',
  'log.kind.data': 'Importaciones, exportaciones y comprobaciones',
  'log.filter.since': 'Desde',
  'log.since.any': 'Cualquier momento',
  'log.since.day': 'Las últimas 24 horas',
  'log.since.week': 'Los últimos 7 días',
  'log.since.month': 'Los últimos 30 días',
  'log.clear': 'Quitar filtros',
  'log.selfNote':
    'Leer el registro también queda registrado, así que sus propias lecturas aparecen aquí.',
  'log.allBusinesses': 'Todos los negocios',
  'log.empty': 'Nada coincide.',
  'log.loading': 'el registro',

  'businesses.title': 'Negocios',
  'businesses.count': ({ total }) => `${String(total)} ${plural(total, 'negocio', 'negocios')}`,
  'businesses.needAttention': ({ count }) =>
    `${String(count)} ${plural(count, 'necesita', 'necesitan')} su atención`,
  'businesses.search': 'Buscar un negocio',
  'businesses.stage': 'Etapa',
  'businesses.stage.all': 'Todos',
  'businesses.stage.setup': 'Preparándose',
  'businesses.stage.live': 'En marcha',
  'businesses.stage.attention': 'Necesitan atención',
  'businesses.showTest': 'Mostrar negocios de prueba',
  'businesses.add': 'Añadir un negocio',
  'businesses.list': 'Negocios',
  'businesses.col.stage': 'Etapa',
  'businesses.col.next': 'Siguiente',
  'businesses.col.health': 'Salud',
  'businesses.demo': 'de muestra',
  'businesses.test': 'prueba',
  'businesses.protected': 'Protegido',
  'businesses.health.ok': 'Todo bien',
  'businesses.health.attention': ({ count }) =>
    count <= 0 ? 'Conviene mirarlo' : `${String(count)} por revisar`,
  'businesses.health.failing': ({ count }) =>
    count <= 0 ? 'Algo va mal' : `${String(count)} ${plural(count, 'problema', 'problemas')}`,
  'businesses.health.unknown': 'Sin revisar',
  'businesses.health.why': 'Por qué',
  'businesses.healthUnavailable':
    'No se pudo leer la salud, así que ningún negocio aparece como bien hasta que se pueda.',
  'businesses.sortWorst': 'Peores primero',
  'businesses.attentionPageOnly': 'Solo se revisan los negocios de esta página.',
  'businesses.empty': 'Todavía no hay negocios. Añada uno para empezar.',
  'businesses.emptyFiltered': 'Ningún negocio coincide.',
  'businesses.emptyAttention': 'Nada en esta página necesita su atención.',
  'businesses.next.fix': 'Arreglar:',
  'businesses.next.more': ({ count }) => ` y ${String(count)} más`,
  'businesses.next.waiting': 'Esperando al dueño o la dueña',
  'businesses.next.questionnaire': ({ answered, total }) =>
    total <= 0
      ? 'para que abra su cuestionario'
      : `para que termine su cuestionario (${String(answered)} de ${String(total)} respondidas)`,
  'businesses.next.review': 'Revisar',
  'businesses.next.reviewWhat': 'las respuestas del cuestionario que devolvieron',
  'businesses.next.setup': 'Seguir preparando:',
  'businesses.next.setupWhat': 'lo que venden, su horario y sus accesos',
  'businesses.next.importing': 'Trayendo su agenda:',
  'businesses.next.importingWhat': 'ponerlo en marcha cuando esté comprobada',
  'businesses.next.paused': 'En pausa:',
  'businesses.next.pausedWhat': 'reanudar cuando estén listos',
  'businesses.next.offboarded': 'Nada que hacer: se han ido',
  'businesses.next.nothing': 'Nada que hacer',
  'businesses.next.checking': 'Revisando…',
  'businesses.reason.webhook_exhausted': 'los avisos de reservas a un socio dejaron de llegar',
  'businesses.reason.endpoint_failing':
    'la dirección de un socio para avisos de reservas sigue fallando',
  'businesses.reason.webhook_failed': 'un aviso de reserva a un socio falló y se está reintentando',
  'businesses.reason.webhook_stuck':
    'los avisos de reservas a un socio llevan más de media hora sin salir',
  'businesses.reason.text_exhausted':
    'no se pudo enviar un mensaje a un cliente, ni tras todos los reintentos',
  'businesses.reason.text_failed': 'un mensaje a un cliente falló y se está reintentando',
  'businesses.reason.text_stuck': 'los mensajes a clientes llevan más de media hora sin salir',
  'businesses.reason.request_overdue': 'una solicitud de reserva se quedó sin respuesta a tiempo',
  'businesses.reason.site_not_served': 'su dirección web no funciona',
};
